'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';

/**
 * 글 한 건의 **썸네일 한 칸**.
 *
 * ⛔ **그림을 자르지 않는다**(`objectFit: contain`). 세로 4:5·9:16 이 대부분이라 자르면
 *   훅이 담긴 윗부분이 날아간다 — 높이 상한만 두고 그 안에 담는다(양옆이 비는 편이 낫다).
 * ⚠️ 게으른 로딩이다 — 수십 장이 한 번에 붙으면 스크롤이 무너진다.
 * ⚠️ 플랫폼 CDN 주소는 며칠 뒤 만료된다 — 깨진 아이콘 대신 **글**로 자리를 지킨다.
 */
export function PostThumb({
  src,
  alt = '',
  text,
  isVideo = false,
  maxHeight = POST_THUMB_MAX_HEIGHT,
  radius = 1.5,
}: {
  src: string;
  alt?: string;
  /** 그림이 없거나 깨졌을 때 대신 읽는 글 */
  text?: string;
  isVideo?: boolean;
  maxHeight?: number;
  radius?: number;
}) {
  const [failed, setFailed] = useState(false);
  const shown = failed ? '' : src;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        borderRadius: radius,
        overflow: 'hidden',
        bgcolor: 'background.neutral',
      }}
    >
      {shown ? (
        <Box
          component="img"
          src={shown}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: 'auto', maxHeight, objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minHeight: 140,
            p: 1.5,
            color: 'text.disabled',
            typography: 'uiMeta',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              typography: 'uiMeta',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {text || '그림이 없는 글입니다.'}
          </Typography>
        </Box>
      )}

      {isVideo && (
        <Iconify
          icon="solar:play-circle-linear"
          width={28}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.92)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))' }}
        />
      )}
    </Box>
  );
}

/** 썸네일 높이 상한 — 데일리 지면과 같은 값 */
export const POST_THUMB_MAX_HEIGHT = 340;
